<template>
    <div :class="$style.container">
        <!-- 통계 탭 네비게이션 -->
        <StatsTabs />

        <!-- 로딩 상태 -->
        <div v-if="loading">
            <LoadingScreen loading-text="라이브 통계를 불러오는 중..." />
        </div>

        <!-- 에러 상태 -->
        <div v-else-if="error" :class="$style.error">
            <p>{{ error }}</p>
            <button @click="loadLiveStats" :class="$style.retryButton">다시 시도</button>
        </div>

        <!-- 통계 데이터 -->
        <div v-else>
            <div :class="$style.header">
                <h1 :class="$style.title">🎤 라이브 통계</h1>
                <p :class="$style.subtitle">등록된 공연 정보 및 현황 분석</p>
            </div>

            <!-- 필터 바 -->
            <div :class="$style.filterBar">
                <!-- 상태 필터 -->
                <div :class="$style.statusFilter">
                    <button :class="[$style.filterBtn, { [$style.activeFilter]: statusFilter === 'all' }]"
                        @click="setStatusFilter('all')">
                        전체
                        <span :class="$style.filterCount">{{ totalCount }}</span>
                    </button>
                    <button
                        :class="[$style.filterBtn, $style.approvedFilter, { [$style.activeFilter]: statusFilter === 'approved' }]"
                        @click="setStatusFilter('approved')">
                        승인됨
                        <span :class="$style.filterCount">{{ approvedCount }}</span>
                    </button>
                    <button
                        :class="[$style.filterBtn, $style.pendingFilter, { [$style.activeFilter]: statusFilter === 'pending' }]"
                        @click="setStatusFilter('pending')">
                        대기중
                        <span :class="$style.filterCount">{{ pendingCount }}</span>
                    </button>
                </div>

                <button @click="loadLiveStats" :class="$style.refreshButton">
                    🔄 새로고침
                </button>
            </div>

            <!-- 요약 카드 -->
            <div :class="$style.summary">
                <div :class="$style.summaryCard">
                    <h3>전체 공연</h3>
                    <p :class="$style.summaryNumber">{{ totalCount }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>예정 공연</h3>
                    <p :class="[$style.summaryNumber, $style.upcomingColor]">{{ upcomingCount }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>진행중</h3>
                    <p :class="[$style.summaryNumber, $style.ongoingColor]">{{ ongoingCount }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>종료된 공연</h3>
                    <p :class="[$style.summaryNumber, $style.endedColor]">{{ endedCount }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>이번 달 공연</h3>
                    <p :class="[$style.summaryNumber, $style.monthColor]">{{ thisMonthCount }}</p>
                </div>
            </div>

            <!-- 상세 통계 그리드 -->
            <div :class="$style.statsGrid">
                <!-- 장소별 공연 수 -->
                <div :class="$style.statsCard">
                    <h4>📍 장소별 공연 수 (상위 15개)</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, place) in topPlaces" :key="place" :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ place }}</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="$style.statsBar" :style="{ width: `${(count / maxPlaceCount) * 100}%` }">
                                </div>
                            </div>
                            <span :class="$style.statsValue">{{ count }}회</span>
                        </div>
                        <div v-if="Object.keys(topPlaces).length === 0" :class="$style.emptyState">
                            데이터가 없습니다
                        </div>
                    </div>
                </div>

                <!-- 아티스트별 공연 수 -->
                <div :class="$style.statsCard">
                    <h4>🎵 아티스트별 공연 수 (상위 15개)</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, artist) in topArtists" :key="artist" :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ artist }}</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="[$style.statsBar, $style.artistBar]"
                                    :style="{ width: `${(count / maxArtistCount) * 100}%` }"></div>
                            </div>
                            <span :class="[$style.statsValue, $style.artistValue]">{{ count }}회</span>
                        </div>
                        <div v-if="Object.keys(topArtists).length === 0" :class="$style.emptyState">
                            데이터가 없습니다
                        </div>
                    </div>
                </div>

                <!-- 공연 유형별 -->
                <div :class="$style.statsCard">
                    <h4>🎭 공연 유형별</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, type) in byType" :key="type" :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ type }}</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="[$style.statsBar, $style.typeBar]"
                                    :style="{ width: `${(count / filteredLives.length) * 100}%` }"></div>
                            </div>
                            <span :class="$style.statsValue">{{ count }}</span>
                            <span :class="$style.statsPercent">{{ Math.round((count / filteredLives.length) * 100)
                                }}%</span>
                        </div>
                        <div v-if="Object.keys(byType).length === 0" :class="$style.emptyState">
                            데이터가 없습니다
                        </div>
                    </div>
                </div>

                <!-- 예약 가능 현황 -->
                <div :class="$style.statsCard">
                    <h4>🎫 예약 가능 현황</h4>
                    <div :class="$style.statsList">
                        <div :class="$style.statsItem">
                            <span :class="$style.statsLabel">예약 가능</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="[$style.statsBar, $style.ticketOnBar]"
                                    :style="{ width: `${(ticketOnCount / filteredLives.length) * 100}%` }"></div>
                            </div>
                            <span :class="[$style.statsValue, $style.ticketOnValue]">{{ ticketOnCount }}</span>
                        </div>
                        <div :class="$style.statsItem">
                            <span :class="$style.statsLabel">예약 불가</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="[$style.statsBar, $style.ticketOffBar]"
                                    :style="{ width: `${((filteredLives.length - ticketOnCount) / filteredLives.length) * 100}%` }">
                                </div>
                            </div>
                            <span :class="$style.statsValue">{{ filteredLives.length - ticketOnCount }}</span>
                        </div>
                    </div>
                </div>

                <!-- 지역별 공연 분포 -->
                <div :class="$style.statsCard">
                    <h4>🗺️ 지역별 공연 분포</h4>
                    <div :class="$style.statsList">
                        <div v-for="(count, region) in byRegion" :key="region" :class="$style.statsItem">
                            <span :class="$style.statsLabel">{{ region }}</span>
                            <div :class="$style.statsBarWrapper">
                                <div :class="[$style.statsBar, $style.regionBar]"
                                    :style="{ width: `${(count / filteredLives.length) * 100}%` }"></div>
                            </div>
                            <span :class="$style.statsValue">{{ count }}</span>
                            <span :class="$style.statsPercent">{{ Math.round((count / filteredLives.length) * 100)
                                }}%</span>
                        </div>
                        <div v-if="Object.keys(byRegion).length === 0" :class="$style.emptyState">
                            데이터가 없습니다
                        </div>
                    </div>
                </div>
            </div>

            <!-- 월별 추이 -->
            <div :class="$style.timelineSection">
                <h3 :class="$style.sectionTitle">📈 월별 공연 추이</h3>
                <div :class="$style.timeline">
                    <div v-for="(count, month) in sortedMonthly" :key="month" :class="$style.timelineItem">
                        <span :class="$style.timelineDate">{{ formatMonth(month) }}</span>
                        <div :class="$style.timelineBarWrapper">
                            <div :class="$style.timelineBar" :style="{ width: `${(count / maxMonthlyCount) * 100}%` }">
                            </div>
                        </div>
                        <span :class="$style.timelineCount">{{ count }}</span>
                    </div>
                    <div v-if="Object.keys(sortedMonthly).length === 0" :class="$style.emptyState">
                        데이터가 없습니다
                    </div>
                </div>
            </div>

            <!-- 최근 등록된 공연 -->
            <div :class="$style.recentSection">
                <h3 :class="$style.sectionTitle">🆕 최근 등록된 공연 (최근 10개)</h3>
                <div :class="$style.recentList">
                    <div v-for="live in recentLives" :key="live.id" :class="$style.recentItem">
                        <div :class="$style.recentInfo">
                            <span :class="$style.recentTitle">{{ live.title }}</span>
                            <span :class="$style.recentMeta">
                                {{ live.place }} · {{ formatDate(live.date) }}
                            </span>
                        </div>
                        <div :class="$style.recentBadges">
                            <span v-if="live.approved" :class="[$style.badge, $style.approvedBadge]">승인</span>
                            <span v-else :class="[$style.badge, $style.pendingBadge]">대기</span>
                            <span :class="[$style.badge, getDaysLeftClass(live.date)]">{{ getDaysLeft(live.date)
                                }}</span>
                        </div>
                    </div>
                    <div v-if="recentLives.length === 0" :class="$style.emptyState">
                        데이터가 없습니다
                    </div>
                </div>
            </div>

            <!-- Slug 마이그레이션 -->
            <div :class="$style.migrationSection">
                <div :class="$style.migrationHeader">
                    <div>
                        <h3 :class="$style.sectionTitle" style="margin-bottom: 4px;">🔧 Slug 마이그레이션</h3>
                        <p :class="$style.migrationDesc">
                            전체 문서에 createdAt 오름차순 기준으로 순번(1, 2, 3…)을 부여합니다.
                            기존 slug도 덮어씁니다.
                        </p>
                    </div>
                    <button :class="[$style.migrateBtn, { [$style.migrateBtnRunning]: migrating }]"
                        :disabled="migrating" @click="migrateSlugs">
                        {{ migrating ? `처리 중… (${migrateProgress}/${migrateTotalCount})` : 'Slug 일괄 부여' }}
                    </button>
                </div>

                <!-- 진행 바 -->
                <div v-if="migrating || migrateResult" :class="$style.migrationProgress">
                    <div v-if="migrating" :class="$style.progressBarWrap">
                        <div :class="$style.progressBar"
                            :style="{ width: migrateTotalCount ? `${(migrateProgress / migrateTotalCount) * 100}%` : '0%' }">
                        </div>
                    </div>
                    <p v-if="migrateResult"
                        :class="[$style.migrateResult, migrateResult.success ? $style.migrateSuccess : $style.migrateError]">
                        {{ migrateResult.message }}
                    </p>
                </div>

                <!-- 미리보기 -->
                <div v-if="migrationPreview.length > 0" :class="$style.migrationPreview">
                    <p :class="$style.previewLabel">부여될 slug 미리보기 (전체 {{ migrationPreview.length }}건)</p>
                    <div :class="$style.previewList">
                        <div v-for="item in migrationPreview" :key="item.id" :class="$style.previewItem">
                            <span :class="$style.previewSlug">{{ item.slug }}</span>
                            <span :class="$style.previewTitle">{{ item.title }}</span>
                            <span :class="$style.previewDate">{{ item.createdAt }}</span>
                        </div>
                    </div>
                </div>
                <button v-if="!migrating" :class="$style.previewBtn" @click="previewMigration">
                    미리보기
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import StatsTabs from "@/components/StatsTabs.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import { useUserStore } from "@/store/user";
import { firestore } from "/firebaseConfig.js";
import { collection, getDocs, doc, writeBatch } from "firebase/firestore";

export default {
    name: "LiveStats",
    components: {
        StatsTabs,
        LoadingScreen,
    },
    data() {
        return {
            loading: false,
            error: null,
            lives: [],
            statusFilter: "all", // all, approved, pending
            // 마이그레이션
            migrating: false,
            migrateProgress: 0,
            migrateTotalCount: 0,
            migrateResult: null,
            migrationPreview: [],
        };
    },
    computed: {
        // 필터된 라이브 목록
        filteredLives() {
            if (this.statusFilter === "all") return this.lives;
            if (this.statusFilter === "approved") {
                return this.lives.filter(live => live.approved);
            }
            if (this.statusFilter === "pending") {
                return this.lives.filter(live => !live.approved);
            }
            return this.lives;
        },

        // 총 개수
        totalCount() {
            return this.lives.length;
        },

        // 승인된 공연 수
        approvedCount() {
            return this.lives.filter(live => live.approved).length;
        },

        // 대기중 공연 수
        pendingCount() {
            return this.lives.filter(live => !live.approved).length;
        },

        // 예정 공연 수 (오늘 이후)
        upcomingCount() {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return this.filteredLives.filter(live => {
                if (!live.date) return false;
                const liveDate = new Date(live.date);
                return liveDate > today;
            }).length;
        },

        // 진행중 공연 수 (오늘)
        ongoingCount() {
            const today = new Date().toISOString().split('T')[0];
            return this.filteredLives.filter(live => {
                if (!live.date) return false;
                return live.date === today;
            }).length;
        },

        // 종료된 공연 수
        endedCount() {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return this.filteredLives.filter(live => {
                if (!live.date) return false;
                const liveDate = new Date(live.date);
                return liveDate < today;
            }).length;
        },

        // 이번 달 공연 수
        thisMonthCount() {
            const now = new Date();
            const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            return this.filteredLives.filter(live => {
                if (!live.date) return false;
                return live.date.startsWith(thisMonth);
            }).length;
        },

        // 장소별 통계
        byPlace() {
            const stats = {};
            this.filteredLives.forEach(live => {
                const place = live.place || '미지정';
                stats[place] = (stats[place] || 0) + 1;
            });
            return stats;
        },

        // 상위 15개 장소
        topPlaces() {
            const entries = Object.entries(this.byPlace);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries.slice(0, 15));
        },

        // 장소 최대값
        maxPlaceCount() {
            const values = Object.values(this.topPlaces);
            return Math.max(...values, 1);
        },

        // 아티스트별 통계 (lineup 필드 사용)
        byArtist() {
            const stats = {};
            this.filteredLives.forEach(live => {
                const lineup = live.lineup || [];
                if (Array.isArray(lineup)) {
                    lineup.forEach(artistName => {
                        if (artistName && typeof artistName === 'string') {
                            const trimmedName = artistName.trim();
                            if (trimmedName) {
                                stats[trimmedName] = (stats[trimmedName] || 0) + 1;
                            }
                        }
                    });
                } else if (typeof lineup === 'string' && lineup) {
                    stats[lineup.trim()] = (stats[lineup.trim()] || 0) + 1;
                }
            });
            return stats;
        },

        // 상위 15개 아티스트
        topArtists() {
            const entries = Object.entries(this.byArtist);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries.slice(0, 15));
        },

        // 아티스트 최대값
        maxArtistCount() {
            const values = Object.values(this.topArtists);
            return Math.max(...values, 1);
        },

        // 공연 유형별 통계
        byType() {
            const stats = {};
            this.filteredLives.forEach(live => {
                const type = live.type || '일반';
                stats[type] = (stats[type] || 0) + 1;
            });
            // 정렬
            const entries = Object.entries(stats);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries);
        },

        // 티켓 ON 개수
        ticketOnCount() {
            return this.filteredLives.filter(live => live.ticketOn).length;
        },

        // 지역별 통계 (장소 기반으로 추정)
        byRegion() {
            const stats = {};
            const regionMap = {
                '서울': ['서울', '홍대', '신촌', '이태원', '강남', '잠실', '합정', '신사', '건대', '동대문', '종로', '명동', '혜화', '마포', '영등포', '용산', '서초', '성동', '광진', '송파'],
                '경기': ['경기', '수원', '성남', '안양', '부천', '용인', '인천', '고양', '일산', '분당', '안산', '화성', '평택'],
                '부산': ['부산', '해운대', '서면', '광안리', '남포동'],
                '대구': ['대구', '동성로'],
                '대전': ['대전', '둔산'],
                '광주': ['광주', '충장로'],
                '기타': [],
            };

            this.filteredLives.forEach(live => {
                const place = live.place || '';
                let matched = false;

                for (const [region, keywords] of Object.entries(regionMap)) {
                    if (region === '기타') continue;
                    if (keywords.some(keyword => place.includes(keyword))) {
                        stats[region] = (stats[region] || 0) + 1;
                        matched = true;
                        break;
                    }
                }

                if (!matched) {
                    stats['기타'] = (stats['기타'] || 0) + 1;
                }
            });

            // 정렬
            const entries = Object.entries(stats);
            entries.sort((a, b) => b[1] - a[1]);
            return Object.fromEntries(entries);
        },

        // 월별 통계
        byMonth() {
            const stats = {};
            this.filteredLives.forEach(live => {
                if (!live.date) return;
                // 날짜 형식 정규화 (YYYY.MM.DD 또는 YYYY-MM-DD 모두 지원)
                const normalizedDate = live.date.replace(/\./g, '-');
                const month = normalizedDate.substring(0, 7); // YYYY-MM
                if (month.match(/^\d{4}-\d{2}$/)) {
                    stats[month] = (stats[month] || 0) + 1;
                }
            });
            return stats;
        },

        // 정렬된 월별 통계
        sortedMonthly() {
            const entries = Object.entries(this.byMonth);
            entries.sort((a, b) => a[0].localeCompare(b[0]));
            // 최근 12개월만
            return Object.fromEntries(entries.slice(-12));
        },

        // 월별 최대값
        maxMonthlyCount() {
            const values = Object.values(this.sortedMonthly);
            return Math.max(...values, 1);
        },

        // 최근 등록된 공연 (createdAt 기준)
        recentLives() {
            const sorted = [...this.filteredLives].sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
            });
            return sorted.slice(0, 10);
        },
    },
    async mounted() {
        // 접근 권한 체크
        const userStore = useUserStore();
        if (userStore.auth !== 'admin') {
            this.$alert("접근 권한이 없습니다.");
            this.$router.push("/");
            return;
        }

        await this.loadLiveStats();
    },
    methods: {
        async loadLiveStats() {
            this.loading = true;
            this.error = null;
            try {
                // 모든 라이브 가져오기 (orderBy 제거 - date 필드가 없는 문서도 포함)
                const snapshot = await getDocs(collection(firestore, "lives"));

                // 클라이언트에서 날짜순 정렬 (date 필드가 없는 문서도 포함)
                this.lives = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                })).sort((a, b) => {
                    if (!a.date && !b.date) return 0;
                    if (!a.date) return 1;
                    if (!b.date) return -1;
                    return b.date.localeCompare(a.date);
                });
            } catch (error) {
                console.error("라이브 통계 로드 오류:", error);
                this.error = "라이브 통계를 불러오는 중 오류가 발생했습니다.";
            } finally {
                this.loading = false;
            }
        },

        setStatusFilter(filter) {
            this.statusFilter = filter;
        },

        formatMonth(monthStr) {
            const [year, month] = monthStr.split('-');
            return `${year.slice(2)}년 ${parseInt(month)}월`;
        },

        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        },

        getDaysLeft(dateStr) {
            if (!dateStr) return '-';
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const liveDate = new Date(dateStr);
            liveDate.setHours(0, 0, 0, 0);
            const diff = Math.ceil((liveDate - today) / (1000 * 60 * 60 * 24));

            if (diff > 0) return `D-${diff}`;
            if (diff === 0) return 'D-DAY';
            return '종료';
        },

        getDaysLeftClass(dateStr) {
            const daysLeft = this.getDaysLeft(dateStr);
            if (daysLeft === 'D-DAY') return this.$style.ddayBadge;
            if (daysLeft === '종료') return this.$style.endedBadge;
            return this.$style.upcomingBadge;
        },

        // createdAt 기준 오름차순으로 정렬된 slug 없는 문서 목록 반환
        async fetchAllDocsSorted() {
            const snapshot = await getDocs(collection(firestore, "lives"));
            const docs = snapshot.docs.map(d => ({
                id: d.id,
                title: d.data().title || "(제목 없음)",
                createdAt: d.data().createdAt,
            }));

            // 전체 문서를 createdAt 오름차순 정렬
            docs.sort((a, b) => {
                const ta = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
                const tb = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
                return ta - tb;
            });

            return docs;
        },

        async previewMigration() {
            this.migrateResult = null;
            const docs = await this.fetchAllDocsSorted();
            this.migrationPreview = docs.map((d, i) => {
                const ts = d.createdAt?.toMillis?.() ?? d.createdAt?.seconds * 1000 ?? null;
                return {
                    id: d.id,
                    slug: i + 1,
                    title: d.title,
                    createdAt: ts ? new Date(ts).toLocaleDateString("ko-KR") : "-",
                };
            });
        },

        async migrateSlugs() {
            if (this.migrating) return;
            const confirmed = window.confirm(
                `전체 문서에 createdAt 오름차순으로 순번(slug)을 부여합니다.\n기존 slug도 덮어씁니다. 계속하시겠습니까?`
            );
            if (!confirmed) return;

            this.migrating = true;
            this.migrateProgress = 0;
            this.migrateResult = null;
            this.migrationPreview = [];

            try {
                const docs = await this.fetchAllDocsSorted();
                this.migrateTotalCount = docs.length;

                // Firestore writeBatch는 최대 500건 — 500건씩 나눠 처리
                const BATCH_SIZE = 500;
                let updated = 0;

                for (let i = 0; i < docs.length; i += BATCH_SIZE) {
                    const chunk = docs.slice(i, i + BATCH_SIZE);
                    const batch = writeBatch(firestore);

                    chunk.forEach((d, idx) => {
                        const slugNum = i + idx + 1;
                        batch.update(doc(firestore, "lives", d.id), { slug: slugNum });
                    });

                    await batch.commit();
                    updated += chunk.length;
                    this.migrateProgress = updated;
                }

                this.migrateResult = {
                    success: true,
                    message: `✅ ${updated}건 전체에 slug(1 ~ ${updated})를 부여했습니다.`,
                };
                // 통계 목록 새로고침
                await this.loadLiveStats();
            } catch (err) {
                console.error("마이그레이션 오류:", err);
                this.migrateResult = { success: false, message: `❌ 오류 발생: ${err.message}` };
            } finally {
                this.migrating = false;
            }
        },
    },
};
</script>

<style module>
/* ========================================
   반응형 디자인 (최대 1024px)
   - Medium: 481px ~ (기본 스타일)
   - Small: ~ 480px
   ======================================== */

.container {
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    padding: 0px 15px;
}

.header {
    margin-bottom: 20px;
}

.title {
    font-size: 1.5rem;
    margin: 0 0 6px 0;
    color: #333;
}

.subtitle {
    color: #666;
    margin: 0;
    font-size: 0.85rem;
}

.error {
    text-align: center;
    padding: 40px;
    color: #d32f2f;
}

.retryButton {
    margin-top: 15px;
    padding: 10px 20px;
    background: #2196f3;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
}

.retryButton:hover {
    background: #1976d2;
}

/* 필터 바 */
.filterBar {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 25px;
    gap: 15px;
    flex-wrap: wrap;
}

/* 상태 필터 */
.statusFilter {
    display: flex;
    gap: 4px;
    background: #f0f0f0;
    padding: 4px;
    border-radius: 10px;
}

.filterBtn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #666;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.filterBtn:hover {
    background: rgba(0, 0, 0, 0.05);
}

.filterBtn.activeFilter {
    background: white;
    color: #333;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filterBtn.approvedFilter.activeFilter {
    color: #4caf50;
}

.filterBtn.pendingFilter.activeFilter {
    color: #ff9800;
}

.filterCount {
    background: rgba(0, 0, 0, 0.08);
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
}

.filterBtn.activeFilter .filterCount {
    background: rgba(0, 0, 0, 0.1);
}

.filterBtn.approvedFilter.activeFilter .filterCount {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
}

.filterBtn.pendingFilter.activeFilter .filterCount {
    background: rgba(255, 152, 0, 0.15);
    color: #ff9800;
}

.refreshButton {
    padding: 10px 20px;
    background: #4caf50;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background 0.2s;
}

.refreshButton:hover {
    background: #43a047;
}

/* 요약 카드 */
.summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 15px;
    margin-bottom: 30px;
}

.summaryCard {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    text-align: center;
    border: 1px solid #e8e8e8;
}

.summaryCard h3 {
    margin: 0 0 8px 0;
    font-size: 0.8rem;
    color: #666;
    font-weight: 600;
}

.summaryNumber {
    font-size: 1.5rem;
    font-weight: bold;
    color: #4caf50;
    margin: 0;
}

.upcomingColor {
    color: #2196f3 !important;
}

.ongoingColor {
    color: #ff5722 !important;
}

.endedColor {
    color: #9e9e9e !important;
}

.monthColor {
    color: #9c27b0 !important;
}

/* 통계 그리드 */
.statsGrid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 20px;
}

.statsCard {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    min-width: 100%;
}

.statsCard h4 {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    color: #333;
    font-weight: 600;
}

.statsList {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 250px;
    overflow-y: auto;
}

.statsItem {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f8f9fa;
    border-radius: 8px;
    font-size: 0.85rem;
}

.statsLabel {
    flex: 1;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 80px;
}

.statsBarWrapper {
    flex: 2;
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.statsBar {
    height: 100%;
    background: linear-gradient(90deg, #4caf50, #81c784);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.artistBar {
    background: linear-gradient(90deg, #9c27b0, #ba68c8);
}

.typeBar {
    background: linear-gradient(90deg, #ff9800, #ffb74d);
}

.ticketOnBar {
    background: linear-gradient(90deg, #4caf50, #81c784);
}

.ticketOffBar {
    background: linear-gradient(90deg, #9e9e9e, #bdbdbd);
}

.regionBar {
    background: linear-gradient(90deg, #00bcd4, #4dd0e1);
}

.statsValue {
    color: #4caf50;
    font-weight: 600;
    flex-shrink: 0;
    min-width: 50px;
    text-align: right;
}

.artistValue {
    color: #9c27b0;
}

.ticketOnValue {
    color: #4caf50;
}

.statsPercent {
    color: #888;
    font-size: 0.8rem;
    flex-shrink: 0;
    min-width: 40px;
    text-align: right;
}

.emptyState {
    text-align: center;
    color: #999;
    padding: 20px;
    font-size: 0.85rem;
}

/* 타임라인 섹션 */
.timelineSection {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    margin-bottom: 20px;
}

.sectionTitle {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    color: #333;
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 400px;
    overflow-y: auto;
}

.timelineItem {
    display: flex;
    align-items: center;
    gap: 12px;
}

.timelineDate {
    flex-shrink: 0;
    width: 60px;
    font-size: 0.8rem;
    color: #666;
}

.timelineBarWrapper {
    flex: 1;
    height: 20px;
    background: #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
}

.timelineBar {
    height: 100%;
    background: linear-gradient(90deg, #2196f3, #64b5f6);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.timelineCount {
    flex-shrink: 0;
    width: 40px;
    text-align: right;
    font-size: 0.85rem;
    font-weight: 600;
    color: #2196f3;
}

/* 최근 공연 섹션 */
.recentSection {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
}

.recentList {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.recentItem {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    background: #f8f9fa;
    border-radius: 8px;
    gap: 10px;
}

.recentInfo {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
}

.recentTitle {
    font-weight: 600;
    color: #333;
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.recentMeta {
    color: #888;
    font-size: 0.8rem;
}

.recentBadges {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}

.badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
}

.approvedBadge {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
}

.pendingBadge {
    background: rgba(255, 152, 0, 0.15);
    color: #ff9800;
}

.ddayBadge {
    background: rgba(244, 67, 54, 0.15);
    color: #f44336;
}

.endedBadge {
    background: rgba(158, 158, 158, 0.15);
    color: #9e9e9e;
}

.upcomingBadge {
    background: rgba(33, 150, 243, 0.15);
    color: #2196f3;
}

/* ========================================
   Small (~ 480px)
   ======================================== */
@media (max-width: 480px) {
    .container {
        padding: 0 10px;
    }

    .header {
        margin-bottom: 15px;
    }

    .title {
        font-size: 1.3rem;
    }

    .subtitle {
        font-size: 0.8rem;
    }

    .filterBar {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .statusFilter {
        width: 100%;
        justify-content: center;
    }

    .filterBtn {
        flex: 1;
        justify-content: center;
        padding: 8px 6px;
        font-size: 0.75rem;
    }

    .refreshButton {
        width: 100%;
    }

    .summary {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .summaryCard {
        padding: 12px;
    }

    .summaryCard h3 {
        font-size: 0.7rem;
        margin-bottom: 6px;
    }

    .summaryNumber {
        font-size: 1.2rem;
    }

    .statsCard {
        padding: 12px;
    }

    .statsCard h4 {
        font-size: 0.85rem;
        margin-bottom: 10px;
    }

    .statsItem {
        padding: 5px 6px;
        font-size: 0.75rem;
    }

    .timelineSection {
        padding: 12px;
    }

    .sectionTitle {
        font-size: 0.9rem;
    }

    .recentSection {
        padding: 12px;
    }

    .recentItem {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .recentBadges {
        align-self: flex-end;
    }
}

/* ========================================
   Slug 마이그레이션
   ======================================== */
.migrationSection {
    margin-top: 30px;
    padding: 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
}

.migrationHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
}

.migrationDesc {
    color: #888;
    font-size: 0.82rem;
    margin: 0;
    line-height: 1.5;
}

.migrateBtn {
    flex-shrink: 0;
    padding: 9px 20px;
    background: #333;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
}

.migrateBtn:hover:not(:disabled) {
    background: #111;
}

.migrateBtnRunning {
    background: #888;
    cursor: not-allowed;
}

.migrationProgress {
    margin-top: 14px;
}

.progressBarWrap {
    width: 100%;
    height: 6px;
    background: #eee;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
}

.progressBar {
    height: 100%;
    background: #333;
    border-radius: 3px;
    transition: width 0.3s ease;
}

.migrateResult {
    font-size: 0.85rem;
    font-weight: 600;
    margin: 8px 0 0;
}

.migrateSuccess {
    color: #2e7d32;
}

.migrateError {
    color: #c62828;
}

.previewBtn {
    margin-top: 14px;
    padding: 7px 16px;
    background: transparent;
    border: 1px solid #ccc;
    border-radius: 8px;
    font-size: 0.82rem;
    color: #555;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
}

.previewBtn:hover {
    border-color: #333;
    color: #333;
}

.migrationPreview {
    margin-top: 14px;
    border: 1px solid #eee;
    border-radius: 8px;
    overflow: hidden;
}

.previewLabel {
    font-size: 0.78rem;
    color: #888;
    padding: 8px 12px;
    margin: 0;
    background: #fafafa;
    border-bottom: 1px solid #eee;
}

.previewList {
    max-height: 280px;
    overflow-y: auto;
}

.previewItem {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 7px 12px;
    font-size: 0.82rem;
    border-bottom: 1px solid #f3f3f3;
}

.previewItem:last-child {
    border-bottom: none;
}

.previewSlug {
    min-width: 36px;
    font-weight: 700;
    color: #333;
    font-family: monospace;
}

.previewTitle {
    flex: 1;
    color: #444;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.previewDate {
    color: #aaa;
    font-size: 0.75rem;
    flex-shrink: 0;
}
</style>
